/**
 * AnalyticsPageNew — queue time analytics page for admin section.
 *
 * Wires together: park selector, attraction selector, date range picker,
 * queue time time-series chart, and label distribution chart.
 */
import React from 'react';
import { BarChart3, Loader2, MapPin } from 'lucide-react';
import { AdminLayoutNew } from '../design-system/layouts/AdminLayoutNew';
import { useAttractions } from '../hooks/useAttractions';
import { useQueueTimeSeries, useLabelDistribution, useThresholds } from '../hooks/useAnalyticsData';
import { AttractionSelector } from '../components/AttractionSelector';
import { DateRangePicker } from '../components/DateRangePicker';
import { TimeSeriesChart } from '../components/TimeSeriesChart';
import { DistributionChart } from '../components/DistributionChart';
import { InlineDropdown } from '../components/shared/InlineDropdown';
import { useParks } from '../hooks/useParks';

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function AnalyticsPageNew(): React.ReactElement {
  const { data: parks, isLoading: parksLoading } = useParks();

  const DEFAULT_DAYS = 30;

  const [selectedPark, setSelectedPark] = React.useState<string | null>(null);
  const [selectedAttractionId, setSelectedAttractionId] = React.useState('');
  const [dateRange, setDateRange] = React.useState<{ start: string; end: string } | null>(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - DEFAULT_DAYS);
    return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
  });
  const [showCustom, setShowCustom] = React.useState(false);

  // Error toast state
  const [toasts, setToasts] = React.useState<{ id: string; message: string; type: 'error' | 'success' | 'info' }[]>([]);
  const shownErrors = React.useRef<Set<string>>(new Set());

  const addToast = React.useCallback((message: string, type: 'error' | 'success' | 'info' = 'error') => {
    const id = Date.now().toString() + Math.random().toString(36).slice(2);
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 5000);
  }, []);

  // Display range for chart title — shows default 30-day range when user cleared the picker
  const displayRange = React.useMemo(() => {
    if (dateRange) return dateRange;
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - DEFAULT_DAYS);
    return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
  }, [dateRange, DEFAULT_DAYS]);

  // Always fetch with a concrete date range (default 30 days)
  const fetchStart = dateRange?.start ?? displayRange.start;
  const fetchEnd = dateRange?.end ?? displayRange.end;

  const { data: attractions, isLoading: attractionsLoading, isError: attractionsError } = useAttractions(selectedPark);
  const { data: queueTimes, isLoading: queueTimesLoading, isError: queueTimesError } = useQueueTimeSeries(
    selectedAttractionId,
    fetchStart,
    fetchEnd,
  );
  const { data: distribution, isLoading: distributionLoading, isError: distributionError } = useLabelDistribution(
    selectedAttractionId,
    fetchStart,
    fetchEnd,
  );
  const { data: thresholds, isError: thresholdsError } = useThresholds();

  React.useEffect(() => {
    if (attractionsError && !shownErrors.current.has('attractions')) {
      shownErrors.current.add('attractions'); addToast('Failed to load attractions. Please try again.', 'error');
    }
    if (queueTimesError && !shownErrors.current.has('queueTimes')) {
      shownErrors.current.add('queueTimes'); addToast('Failed to load queue time data. Please try again.', 'error');
    }
    if (distributionError && !shownErrors.current.has('distribution')) {
      shownErrors.current.add('distribution'); addToast('Failed to load distribution data. Please try again.', 'error');
    }
    if (thresholdsError && !shownErrors.current.has('thresholds')) {
      shownErrors.current.add('thresholds'); addToast('Failed to load threshold data. Please try again.', 'error');
    }
  }, [attractionsError, queueTimesError, distributionError, thresholdsError, addToast]);

  const hasSelection = !!selectedAttractionId;
  const anyLoading = parksLoading || attractionsLoading || queueTimesLoading || distributionLoading;

  const parkOptions = React.useMemo(
    () =>
      (parks ?? []).map((p) => ({
        value: p.name,
        label: p.name,
        icon: <MapPin className="w-3.5 h-3.5" />,
      })),
    [parks],
  );

  const handleParkChange = React.useCallback((parkId: string) => {
    if (selectedPark === parkId) return;
    setSelectedPark(parkId);
    setSelectedAttractionId('');
  }, [selectedPark]);

  return (
    <AdminLayoutNew>
      {/* Error toast notifications */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm" aria-live="polite">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`flex items-center gap-2 px-4 py-3 rounded-lg border shadow-lg ${toast.type === 'error'
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
                : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200'
              }`}
            role="status"
            aria-live="polite"
          >
            <span className="flex-1 text-sm">{toast.message}</span>
            <button
              type="button"
              onClick={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
              className="ml-2 opacity-70 hover:opacity-100"
              aria-label="Close notification"
            >
              &#x2715;
            </button>
          </div>
        ))}
      </div>

      {anyLoading ? (
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-disney-blue-500" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-disney-blue-500" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Analytics</h1>
          </div>

          {/* Controls row */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Park selector */}
            <InlineDropdown
              value={selectedPark ?? ''}
              options={parkOptions}
              onChange={handleParkChange}
              placeholder="All parks"
              buttonClassName="min-w-[180px]"
            />

            {/* Attraction selector */}
            <AttractionSelector
              value={selectedAttractionId}
              attractions={attractions ?? []}
              onChange={(id) => setSelectedAttractionId(id)}
              isLoading={attractionsLoading}
              park={selectedPark}
            />

            {/* Date range presets + custom picker */}
            <div className="inline-flex items-center gap-1">
              {(
                [
                  { label: '2d', days: 2 },
                  { label: '7d', days: 7 },
                  { label: '30d', days: 30 },
                  { label: '90d', days: 90 },
                ] as const
              ).map((preset) => {
                const range = (() => {
                  const end = new Date();
                  const start = new Date();
                  start.setDate(end.getDate() - preset.days);
                  return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
                })();
                const isActive = dateRange && dateRange.start === range.start && dateRange.end === range.end;
                return (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => setDateRange({ ...range })}
                    className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${isActive
                        ? 'bg-disney-blue-500 text-white border-disney-blue-500'
                        : 'bg-white dark:bg-surface-dark-card text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-disney-blue-300 hover:text-disney-blue-500'
                      }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
              {/* Custom toggle */}
              <button
                type="button"
                onClick={() => {
                  setShowCustom((prev) => !prev);
                  setDateRange(null);
                }}
                className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${showCustom
                    ? 'bg-disney-blue-500 text-white border-disney-blue-500'
                    : 'bg-white dark:bg-surface-dark-card text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-disney-blue-300 hover:text-disney-blue-500'
                  }`}
              >
                Custom
              </button>
              {showCustom && (
                <div className="inline-flex items-center ml-1">
                  <DateRangePicker value={dateRange} onChange={(r) => { if (r) setDateRange(r); }} />
                </div>
              )}
            </div>
          </div>

          {/* Analytics panels */}
          {hasSelection ? (
            <div className="grid grid-cols-1 gap-6">
              {/* Queue time time-series */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark-card">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-disney-blue-500" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Queue Time Trend</h3>
                </div>
                {queueTimesLoading ? (
                  <div className="px-4 py-12 text-center text-sm text-gray-400 dark:text-gray-500">Loading queue times...</div>
                ) : queueTimes && queueTimes.length > 0 ? (
                  <div className="p-4">
                    <TimeSeriesChart
                      data={queueTimes}
                      singleThreshold={thresholds ? thresholds[selectedAttractionId] : undefined}
                      title={`${formatDate(displayRange.start)} — ${formatDate(displayRange.end)}`}
                    />
                  </div>
                ) : (
                  <div className="px-4 py-12 text-center text-sm text-gray-400 dark:text-gray-500">No queue time data available</div>
                )}
              </div>

              {/* Label distribution */}
              {distribution && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark-card">
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-disney-blue-500" />
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Queue Time Distribution</h3>
                  </div>
                  {distributionLoading ? (
                    <div className="px-4 py-12 text-center text-sm text-gray-400 dark:text-gray-500">Loading distribution...</div>
                  ) : (
                    <div className="p-4">
                      <DistributionChart
                        data={distribution}
                        thresholds={thresholds ? thresholds[selectedAttractionId] : undefined}
                        attractionName={attractions?.find((a) => a.id === selectedAttractionId)?.name ?? ''}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* Empty state */
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark-card p-12 text-center">
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Select an attraction to view analytics
              </p>
            </div>
          )}
        </div>
      )}
    </AdminLayoutNew>
  );
}
